import { styled } from '@mui/material';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import {
  FileViewerInitialLoadOverlay,
  FileViewerLoadingSpinner,
  FileViewerToolbarButton,
  FileViewerToolbarDivider,
  FileViewerToolbarSection,
  FileViewerToolbarText,
} from '../viewer-controls';

export {
  FileViewerInitialLoadOverlay as SpreadsheetViewerInitialLoadOverlay,
  FileViewerLoadingSpinner as SpreadsheetViewerLoadingSpinner,
};

const SPREADSHEET_VIEWER_FROZEN_ROW_Z_INDEX = 52;
const SPREADSHEET_VIEWER_FROZEN_COLUMN_Z_INDEX = 42;
const SPREADSHEET_VIEWER_FROZEN_INTERSECTION_Z_INDEX = 62;
const SPREADSHEET_VIEWER_ROW_NUMBERS_Z_INDEX = 72;
const SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT = 12;
const SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT = 32;
const SPREADSHEET_VIEWER_GRID_CHROME_BACKGROUND = '#EDF2F7';
const SPREADSHEET_VIEWER_ROW_INDICATOR_WIDTH = 12;
const SPREADSHEET_VIEWER_ROW_LEGEND_WIDTH = 32;

interface ZoomStyleProps {
  zoomLevel?: number;
}

const shouldForwardZoomProp = (prop: PropertyKey) => prop !== 'zoomLevel';

const getSpreadsheetViewerPx = (value: number, zoomLevel = 1) => getZoomAdjustedPx(value, zoomLevel);

export const SpreadsheetViewerWrapper = styled('div')({
  animation: 'fadeInUp 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
  background: '#F3F8FD',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: '100%',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

export const SpreadsheetViewerStateContainer = styled('div')({
  alignItems: 'center',
  color: '#475569',
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  fontSize: 13,
  gap: 8,
  justifyContent: 'center',
  lineHeight: '18px',
  padding: 24,
  textAlign: 'center',
});

export const SpreadsheetViewerSheetNavigation = styled('div')({
  backgroundColor: '#F3F8FD',
  borderBottom: '1px solid #D8E3EE',
  boxSizing: 'border-box',
  padding: '4px 8px',
  '& [data-testid="tab-navigation__actions"]': {
    flex: '0 0 auto',
    minWidth: 0,
  },
});

export const SpreadsheetViewerWorkbookActions = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ zoomLevel }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: '0 0 auto',
  flexWrap: 'nowrap',
  gap: getSpreadsheetViewerPx(6, zoomLevel),
  justifyContent: 'flex-end',
  minWidth: 0,
  whiteSpace: 'nowrap',
}));

export const SpreadsheetViewerToolbarSection = styled(FileViewerToolbarSection, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ zoomLevel }) => ({
  borderRadius: getSpreadsheetViewerPx(8, zoomLevel),
  flex: '0 0 auto',
  flexWrap: 'nowrap',
  gap: getSpreadsheetViewerPx(3, zoomLevel),
  justifyContent: 'flex-end',
  minHeight: getSpreadsheetViewerPx(28, zoomLevel),
  minWidth: 0,
  padding: `${getSpreadsheetViewerPx(2, zoomLevel)} ${getSpreadsheetViewerPx(4, zoomLevel)}`,
  whiteSpace: 'nowrap',
}));

export const SpreadsheetViewerSearchToolbarSection = styled(SpreadsheetViewerToolbarSection, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ zoomLevel }) => ({
  borderRadius: getSpreadsheetViewerPx(8, zoomLevel),
  gap: getSpreadsheetViewerPx(3, zoomLevel),
  minHeight: getSpreadsheetViewerPx(28, zoomLevel),
  padding: `${getSpreadsheetViewerPx(2, zoomLevel)} ${getSpreadsheetViewerPx(4, zoomLevel)}`,
}));

export const SpreadsheetViewerToolbarButton = styled(FileViewerToolbarButton, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ zoomLevel }) => ({
  borderRadius: getSpreadsheetViewerPx(999, zoomLevel),
  fontSize: getSpreadsheetViewerPx(12, zoomLevel),
  height: getSpreadsheetViewerPx(24, zoomLevel),
  minWidth: getSpreadsheetViewerPx(24, zoomLevel),
  padding: `0 ${getSpreadsheetViewerPx(7, zoomLevel)}`,
  '& svg': {
    fontSize: getSpreadsheetViewerPx(17, zoomLevel),
  },
}));

export const SpreadsheetViewerToolbarDivider = styled(FileViewerToolbarDivider, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ zoomLevel }) => ({
  height: getSpreadsheetViewerPx(14, zoomLevel),
  margin: `0 ${getSpreadsheetViewerPx(2, zoomLevel)}`,
  width: getSpreadsheetViewerPx(1, zoomLevel),
}));

export const SpreadsheetViewerToolbarText = styled(FileViewerToolbarText, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ zoomLevel }) => ({
  fontSize: getSpreadsheetViewerPx(11, zoomLevel),
  padding: `0 ${getSpreadsheetViewerPx(3, zoomLevel)}`,
}));

export const SpreadsheetViewerSearchInput = styled('input', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ zoomLevel }) => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #CFE0F2',
  borderRadius: getSpreadsheetViewerPx(8, zoomLevel),
  boxSizing: 'border-box',
  color: '#0F172A',
  fontSize: getSpreadsheetViewerPx(12, zoomLevel),
  height: getSpreadsheetViewerPx(22, zoomLevel),
  lineHeight: `${getSpreadsheetViewerPx(18, zoomLevel)}px`,
  minWidth: getSpreadsheetViewerPx(112, zoomLevel),
  outline: 'none',
  padding: `0 ${getSpreadsheetViewerPx(7, zoomLevel)}`,
  width: getSpreadsheetViewerPx(132, zoomLevel),
  '&::placeholder': {
    color: '#64748B',
  },
  '&:focus': {
    borderColor: '#037DE8',
    boxShadow: '0 0 0 2px rgba(3, 125, 232, 0.12)',
  },
}));

export const SpreadsheetViewerFormulaBar = styled('div')({
  alignItems: 'center',
  backgroundColor: '#FFFFFF',
  borderBottom: '1px solid #D8E3EE',
  boxSizing: 'border-box',
  color: '#334155',
  display: 'flex',
  fontSize: 12,
  gap: 8,
  minHeight: 30,
  overflow: 'hidden',
  padding: '4px 12px',
});

export const SpreadsheetViewerScrollContainer = styled('div')({
  background: 'linear-gradient(180deg, #F3F8FD 0%, #EEF4F8 100%)',
  boxSizing: 'border-box',
  flex: '1 1 auto',
  minHeight: 0,
  overflow: 'auto',
  padding: '12px',
  position: 'relative',
  scrollbarGutter: 'stable',
  width: '100%',

  '&[data-content-variant="legacy-data-grid"]': {
    background: SPREADSHEET_VIEWER_GRID_CHROME_BACKGROUND,
    isolation: 'isolate',
    paddingLeft: 0,
    paddingTop: 0,
  },

  // Virtualized sheets own both scroll axes. Keep the workbook at the viewer width
  // so the outer container cannot scroll independently of the frozen panes.
  '&[data-content-variant="legacy-data-grid"]:has(> [role="tabpanel"][aria-hidden="false"] .is-virtualized)': {
    overflow: 'hidden',
    scrollbarGutter: 'auto',
  },

  '&[data-content-variant="legacy-data-grid"]:has(> [role="tabpanel"][aria-hidden="false"] .is-virtualized) > [role="tabpanel"][aria-hidden="false"] > div': {
    display: 'block !important',
    width: '100%',
  },

  '&[data-content-variant="legacy-data-grid"]:has(> [role="tabpanel"][aria-hidden="false"] .is-virtualized) [data-testid="spreadsheet-note-xlsx-preview"]': {
    width: '100%',
  },

  '&[data-content-variant="legacy-data-grid"]:has(> [role="tabpanel"][aria-hidden="false"] .is-virtualized) #spreadsheet-table__content-columns .data-grid-container': {
    display: 'block',
    width: '100%',
  },

  '&[data-content-variant="legacy-data-grid"]:has(> [role="tabpanel"][aria-hidden="false"] .is-virtualized) #spreadsheet-table__content-columns .data-grid-container > div': {
    boxSizing: 'border-box',
    width: '100% !important',
  },

  '&[data-content-variant="legacy-data-grid"]::before': {
    backgroundColor: SPREADSHEET_VIEWER_GRID_CHROME_BACKGROUND,
    content: '""',
    display: 'block',
    height: `calc(${SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT}px * var(--xlsx-preview-zoom-scale, 1))`,
    left: `calc(${SPREADSHEET_VIEWER_ROW_INDICATOR_WIDTH}px * var(--xlsx-preview-zoom-scale, 1))`,
    marginBottom: `calc(-${SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT}px * var(--xlsx-preview-zoom-scale, 1))`,
    pointerEvents: 'none',
    position: 'sticky',
    top: 0,
    width: '100%',
    // Row numbers have their own stacking context above frozen cells. The existing
    // header cover must sit above that whole pane, not just its individual cells.
    zIndex: SPREADSHEET_VIEWER_ROW_NUMBERS_Z_INDEX + 1,
  },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"]': {
    minWidth: '100%',
    width: 'fit-content',
  },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .MuiGrid-root': {
    padding: 0,
  },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .titles-columns-container':
    {
      backgroundColor: SPREADSHEET_VIEWER_GRID_CHROME_BACKGROUND,
      backgroundClip: 'padding-box',
      boxShadow: 'none',
      flex: '0 0 auto',
      overflow: 'visible',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] [class*="ContentColumns-columnsContent"]':
    {
      backgroundColor: SPREADSHEET_VIEWER_GRID_CHROME_BACKGROUND,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .titles-columns-container':
    {
      left: 0,
      paddingLeft: SPREADSHEET_VIEWER_ROW_INDICATOR_WIDTH,
      paddingRight: 0,
      position: 'sticky',
      transform: 'translateX(var(--xlsx-title-columns-translate-x, 0px))',
      // This pane is a stacking context: even frozen row/column intersections must pass behind it.
      zIndex: SPREADSHEET_VIEWER_ROW_NUMBERS_Z_INDEX,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .titles-columns-container [id^="row-indicator-"]':
    {
      zIndex: SPREADSHEET_VIEWER_FROZEN_COLUMN_Z_INDEX + 2,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .titles-columns-container td.legend.row-legend':
    {
      backgroundClip: 'padding-box',
      backgroundColor: `${SPREADSHEET_VIEWER_GRID_CHROME_BACKGROUND} !important`,
      position: 'relative',
      zIndex: SPREADSHEET_VIEWER_FROZEN_COLUMN_Z_INDEX + 1,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-column-legend="α"]':
    {
      backgroundClip: 'border-box',
      backgroundColor: '#FFFFFF !important',
      boxShadow: 'var(--xlsx-frozen-column-shadow, none)',
      left: 'var(--xlsx-frozen-column-left, 0px) !important',
      minWidth: 175,
      position: 'sticky !important',
      transform: 'translateX(var(--xlsx-frozen-pane-translate-x, 0px))',
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_COLUMN_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.legend.col-legend':
    {
      backgroundClip: 'border-box',
      backgroundColor: `${SPREADSHEET_VIEWER_GRID_CHROME_BACKGROUND} !important`,
      boxShadow: [
        '0 var(--xlsx-frozen-row-border-width, 1px) 0 var(--xlsx-frozen-row-border-color, #D8E3EE)',
        'var(--xlsx-frozen-row-shadow, none)',
      ].join(', '),
      color: '#94A3B8 !important',
      position: 'sticky !important',
      top: `var(--xlsx-frozen-row-indicator-offset, ${SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT}px) !important`,
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_ROW_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-internal-reference-cell, &[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"][data-row-index="0"]:not(.col-legend)':
    {
      backgroundClip: 'border-box',
      backgroundColor: '#FFFFFF !important',
      boxShadow: [
        '0 var(--xlsx-frozen-row-border-width, 1px) 0 var(--xlsx-frozen-row-border-color, #D8E3EE)',
        'var(--xlsx-frozen-row-shadow, none)',
      ].join(', '),
      position: 'sticky !important',
      top: `calc(var(--xlsx-frozen-row-indicator-offset, ${SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT}px) + ${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px) !important`,
      verticalAlign: 'middle',
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_ROW_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.legend.col-legend[data-xlsx-frozen-pane-column="true"], &[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-internal-reference-cell[data-xlsx-frozen-pane-column="true"], &[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-row-index="0"][data-xlsx-frozen-pane-column="true"]':
    {
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_INTERSECTION_Z_INDEX} !important`,
    },

  // Match the reference row's grid scope so it cannot override the higher intersection layer.
  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"].col-legend, &[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"].xlsx-internal-reference-cell, &[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"][data-row-index="0"]':
    {
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_INTERSECTION_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-frozen-pane-column="true"]':
    {
      backgroundClip: 'border-box',
      backgroundColor: 'var(--xlsx-frozen-pane-background-color, #FFFFFF) !important',
      isolation: 'isolate',
      left: 'calc(var(--xlsx-frozen-column-left, 0px) + var(--xlsx-frozen-pane-column-offset, 0px)) !important',
      position: 'sticky !important',
      transform: 'translateX(var(--xlsx-frozen-pane-translate-x, 0px))',
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_COLUMN_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-frozen-pane-column="true"]:not(.col-legend):not([data-xlsx-frozen-pane-row="true"])':
    {
      top: 'auto !important',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-pinned-column-passthrough="true"]':
    {
      left: 'auto !important',
      transform: 'none !important',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-pinned-column-passthrough="true"]:not(.col-legend):not([data-xlsx-frozen-pane-row="true"])':
    {
      position: 'static !important',
      top: 'auto !important',
      zIndex: 'auto !important',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-pinned-column-passthrough="true"].col-legend, &[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-pinned-column-passthrough="true"][data-xlsx-frozen-pane-row="true"]':
    {
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_ROW_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-frozen-pane-column-boundary="true"]':
    {
      borderRight:
        'var(--xlsx-frozen-column-border-width, 1px) solid var(--xlsx-frozen-column-border-color, #D8E3EE) !important',
      boxShadow: 'var(--xlsx-frozen-column-shadow, none)',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-frozen-pane-column="true"].col-legend':
    {
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_INTERSECTION_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] tr[data-xlsx-pinned-row-passthrough="true"] > td:not([data-xlsx-frozen-pane-column="true"])':
    {
      position: 'static !important',
      top: 'auto !important',
      zIndex: 'auto !important',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid tr[data-xlsx-pinned-row-passthrough="true"] > td[data-xlsx-frozen-pane-column="true"]:not([data-xlsx-frozen-pane-row="true"])':
    {
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_COLUMN_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-frozen-pane-row="true"]':
    {
      backgroundClip: 'border-box',
      backgroundColor: 'var(--xlsx-frozen-pane-background-color, #FFFFFF) !important',
      position: 'sticky !important',
      top: `calc(var(--xlsx-frozen-row-indicator-offset, ${SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT}px) + var(--xlsx-frozen-pane-row-top, 0px)) !important`,
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_ROW_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-frozen-pane-row-boundary="true"]':
    {
      borderBottom:
        'var(--xlsx-frozen-row-border-width, 1px) solid var(--xlsx-frozen-row-border-color, #D8E3EE) !important',
      boxShadow: 'var(--xlsx-frozen-row-shadow, none)',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-frozen-pane-row="true"].row-legend':
    {
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_INTERSECTION_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"] [data-testid="spreadsheet-note-xlsx-preview"] .titles-columns-container td.legend.row-legend[data-xlsx-frozen-pane-row="true"]':
    {
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_INTERSECTION_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-pane="true"] [data-testid="spreadsheet-note-xlsx-preview"] td[data-xlsx-frozen-pane-column="true"][data-xlsx-frozen-pane-row="true"]':
    {
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_INTERSECTION_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-column="true"][data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"]:not(.col-legend):not([data-xlsx-frozen-pane-column="true"])':
    {
      borderRight: '1px solid #E2E8F0 !important',
      boxShadow: 'none',
      left: 'auto !important',
      transform: 'none',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-column="true"][data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"].col-legend:not([data-xlsx-frozen-pane-column="true"])':
    {
      boxShadow: [
        '0 var(--xlsx-frozen-row-border-width, 1px) 0 var(--xlsx-frozen-row-border-color, #D8E3EE)',
        'var(--xlsx-frozen-row-shadow, none)',
      ].join(', '),
      left: 'auto !important',
      transform: 'none',
      zIndex: SPREADSHEET_VIEWER_FROZEN_ROW_Z_INDEX,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-column="true"][data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"]:not(.col-legend):not([data-xlsx-frozen-pane-column="true"]):not([data-xlsx-frozen-pane-row="true"])':
    {
      zIndex: 'auto',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-column="true"]:not([data-xlsx-custom-freeze-row="true"])[data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"].xlsx-internal-reference-cell:not(.col-legend):not([data-xlsx-frozen-pane-column="true"]):not([data-xlsx-frozen-pane-row="true"])':
    {
      boxShadow: [
        '0 var(--xlsx-frozen-row-border-width, 1px) 0 var(--xlsx-frozen-row-border-color, #D8E3EE)',
        'var(--xlsx-frozen-row-shadow, none)',
      ].join(', '),
      zIndex: SPREADSHEET_VIEWER_FROZEN_ROW_Z_INDEX,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-internal-reference-cell:not([data-xlsx-frozen-pane-row="true"]), &[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"][data-row-index="0"]:not([data-xlsx-frozen-pane-row="true"])':
    {
      boxShadow: 'none',
      top: 'auto !important',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.legend.col-legend:not([data-xlsx-frozen-pane-row="true"])':
    {
      boxShadow: 'none',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-internal-reference-cell:not([data-xlsx-frozen-pane-column="true"]):not([data-xlsx-frozen-pane-row="true"]), &[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"][data-row-index="0"]:not([data-xlsx-frozen-pane-column="true"]):not([data-xlsx-frozen-pane-row="true"])':
    {
      zIndex: 'auto',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"]:not([data-xlsx-custom-freeze-column="true"])[data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"].xlsx-internal-reference-cell, &[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"]:not([data-xlsx-custom-freeze-column="true"])[data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"][data-row-index="0"]':
    {
      boxShadow: 'var(--xlsx-frozen-column-shadow, none)',
      zIndex: SPREADSHEET_VIEWER_FROZEN_COLUMN_Z_INDEX,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"]:not([data-xlsx-custom-freeze-column="true"])[data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"][data-xlsx-frozen-pane-row="true"]':
    {
      zIndex: SPREADSHEET_VIEWER_FROZEN_INTERSECTION_Z_INDEX,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-internal-reference-cell[data-xlsx-frozen-pane-column="true"]:not([data-xlsx-frozen-pane-row="true"])':
    {
      zIndex: SPREADSHEET_VIEWER_FROZEN_COLUMN_Z_INDEX,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .titles-columns-container td.legend.row-legend.col-legend':
    {
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_INTERSECTION_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .titles-columns-container .data-grid tr:nth-of-type(2) td.legend.row-legend':
    {
      backgroundClip: 'padding-box',
      backgroundColor: `${SPREADSHEET_VIEWER_GRID_CHROME_BACKGROUND} !important`,
      boxShadow: [
        '0 var(--xlsx-frozen-row-border-width, 1px) 0 var(--xlsx-frozen-row-border-color, #D8E3EE)',
        'var(--xlsx-frozen-row-shadow, none)',
      ].join(', '),
      color: '#94A3B8 !important',
      position: 'sticky !important',
      top: `calc(var(--xlsx-frozen-row-indicator-offset, ${SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT}px) + ${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px) !important`,
      zIndex: `${SPREADSHEET_VIEWER_FROZEN_INTERSECTION_Z_INDEX} !important`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-custom-freeze-row="true"][data-xlsx-frozen-reference-row="true"] [data-testid="spreadsheet-note-xlsx-preview"] .titles-columns-container .data-grid tr:nth-of-type(2) td.legend.row-legend:not([data-xlsx-frozen-pane-row="true"])':
    {
      boxShadow: 'none',
      top: 'auto !important',
      zIndex: 'auto',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] td.legend.col-legend[data-column-legend="α"] .value-viewer, &[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] td.legend.col-legend[data-column-legend="α"] .text-cell':
    {
      visibility: 'hidden',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] [class*="ContentColumns-columnsContent"] [id^="column-indicator-"]':
    {
      backgroundColor: '#037DE8 !important',
      borderRadius: '3px 3px 0 0 !important',
      boxSizing: 'border-box',
      height: SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT,
      marginBottom: 0,
      marginTop: -SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT,
      maxHeight: SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT,
      minHeight: SPREADSHEET_VIEWER_COLUMN_INDICATOR_HEIGHT,
      position: 'sticky !important',
      top: '0 !important',
      transform: 'translateX(var(--xlsx-column-indicator-translate-x, 0px))',
      // The selection indicator remains interactive and visible above the cover.
      zIndex: SPREADSHEET_VIEWER_ROW_NUMBERS_Z_INDEX + 2,
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid-container': {
    backgroundColor: '#FFFFFF',
    border: '1px solid #D8E3EE',
    borderRadius: 0,
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
    boxSizing: 'border-box',
    display: 'inline-block',
    minWidth: '100%',
    overflow: 'visible',
  },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid': {
    backgroundColor: '#FFFFFF',
    borderCollapse: 'separate',
    borderSpacing: 0,
    color: '#0F172A',
    fontSize: 12,
    lineHeight: '16px',
  },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td': {
    borderBottom: '1px solid #E2E8F0',
    borderRight: '1px solid #E2E8F0',
    boxSizing: 'border-box',
    fontSize: 12,
    lineHeight: '16px',
    minWidth: 42,
    padding: '5px 8px',
    transition: 'background-color 140ms ease, color 140ms ease, box-shadow 140ms ease',
  },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"]:not(.col-legend)':
    {
      borderRight:
        'var(--xlsx-frozen-column-border-width, 1px) solid var(--xlsx-frozen-column-border-color, transparent) !important',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.legend': {
    backgroundColor: `${SPREADSHEET_VIEWER_GRID_CHROME_BACKGROUND} !important`,
    borderRadius: '0 !important',
    boxSizing: 'border-box',
    color: '#94A3B8 !important',
    fontSize: '11px !important',
    fontWeight: 600,
    lineHeight: '12px',
    overflow: 'visible',
    padding: '0 !important',
    textAlign: 'center',
    userSelect: 'none',
    verticalAlign: 'middle',
    WebkitUserSelect: 'none',
  },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.legend .value-viewer, &[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.legend .text-cell':
    {
      alignItems: 'center !important',
      borderRadius: '0 !important',
      boxSizing: 'border-box',
      color: '#94A3B8 !important',
      display: 'flex',
      fontSize: '11px !important',
      height: '100%',
      justifyContent: 'center !important',
      lineHeight: '12px',
      maxWidth: '100%',
      minWidth: 0,
      overflow: 'visible',
      padding: '0 !important',
      textAlign: 'center !important',
      whiteSpace: 'nowrap',
      width: '100%',
      wordBreak: 'normal',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.legend.col-legend':
    {
      borderTop: '1px solid #D8E3EE !important',
      height: `${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px !important`,
      maxHeight: `${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px !important`,
      minHeight: `${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px !important`,
      paddingLeft: 0,
      paddingRight: 0,
      paddingBottom: 0,
      paddingTop: 0,
      verticalAlign: 'middle',
    },

  // Virtualized sheets can append an unlabeled column spacer to this row.
  // It must be compact too, or it stretches the entire legend back to a data-row height.
  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .is-virtualized .data-grid tr:has(> td.col-legend) > td':
    {
      height: `${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px !important`,
      maxHeight: `${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px !important`,
      minHeight: `${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px !important`,
    },

  // The content virtualizer replaces the compact header with a full-height row spacer.
  // Match that extra space in the always-mounted row-number pane while the header is offscreen.
  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .is-virtualized:has([id="spreadsheet-table__content-columns"] .data-grid tr:first-child > td[colspan]) .titles-columns-container table.data-grid':
    {
      marginTop: `calc(var(--virtual-row-height) - ${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px)`,
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td:not(.legend) .value-viewer':
    {
      alignItems: 'center',
      display: 'flex',
      height: '100%',
      justifyContent: 'flex-start',
      minHeight: '100%',
      overflow: 'hidden',
      width: '100%',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-internal-reference-cell .value-viewer, &[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-internal-reference-cell .select-value-viewer, &[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-row-reference-cell .select-value-viewer':
    {
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'flex',
      height: '100%',
      maxHeight: '100%',
      minHeight: 0,
      overflow: 'hidden',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-internal-reference-cell':
    {
      borderTop: '1px solid #D8E3EE !important',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-internal-reference-cell[data-xlsx-selected-year-reference="true"]':
    {
      backgroundColor: '#E8F3FF !important',
      boxShadow: [
        'inset 0 0 0 1.5px #037DE8',
        '0 1px 0 rgba(3, 125, 232, 0.18)',
        'var(--xlsx-frozen-row-shadow, none)',
      ].join(', '),
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.legend.row-legend':
    {
      maxWidth: SPREADSHEET_VIEWER_ROW_LEGEND_WIDTH,
      minWidth: SPREADSHEET_VIEWER_ROW_LEGEND_WIDTH,
      padding: 0,
      width: SPREADSHEET_VIEWER_ROW_LEGEND_WIDTH,
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.legend.row-legend.col-legend':
    {
      height: `${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px !important`,
      maxHeight: `${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px !important`,
      maxWidth: SPREADSHEET_VIEWER_ROW_LEGEND_WIDTH,
      minHeight: `${SPREADSHEET_VIEWER_COLUMN_LEGEND_HEIGHT}px !important`,
      minWidth: SPREADSHEET_VIEWER_ROW_LEGEND_WIDTH,
      width: SPREADSHEET_VIEWER_ROW_LEGEND_WIDTH,
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.selected': {
    boxShadow: 'inset 0 0 0 1.5px #037DE8',
    color: 'inherit',
  },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td[data-column-legend="α"].selected':
    {
      boxShadow: 'inset 0 0 0 1.5px #037DE8, var(--xlsx-frozen-column-shadow-accent, 0 0 0 0 transparent)',
    },

  '&[data-content-variant="legacy-data-grid"] [data-xlsx-frozen-reference-column="true"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid tr:has(td.selected:not(.col-legend)) td[data-column-legend="α"]:not(.col-legend)':
    {
      boxShadow: 'inset 0 0 0 1.5px #037DE8, var(--xlsx-frozen-column-shadow-accent, 0 0 0 0 transparent)',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.cell-copy-feedback':
    {
      animation: 'xlsxCopyFeedback 900ms ease-out',
      boxShadow: 'inset 0 0 0 1.5px #037DE8, 0 0 0 3px rgba(34, 197, 94, 0.2)',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-search-match':
    {
      boxShadow: 'inset 0 0 0 1.5px rgba(250, 204, 21, 0.95) !important',
      position: 'relative',
      zIndex: 2,
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.xlsx-search-match-current':
    {
      boxShadow: 'inset 0 0 0 2px #037DE8, 0 0 0 3px rgba(3, 125, 232, 0.16) !important',
      position: 'relative',
      zIndex: 3,
    },

  '@keyframes xlsxCopyFeedback': {
    '0%': {
      boxShadow: 'inset 0 0 0 1.5px #22C55E, 0 0 0 0 rgba(34, 197, 94, 0.28)',
    },
    '42%': {
      boxShadow: 'inset 0 0 0 1.5px #22C55E, 0 0 0 5px rgba(34, 197, 94, 0.18)',
    },
    '100%': {
      boxShadow: 'inset 0 0 0 1.5px #037DE8, 0 0 0 3px rgba(34, 197, 94, 0)',
    },
  },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.cell-with-references':
    {
      color: '#16A34A !important',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .data-grid td.cell-with-references .value-viewer .text-cell':
    {
      color: 'inherit !important',
    },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .value-viewer': {
    maxWidth: '100%',
  },

  '&[data-content-variant="legacy-data-grid"] [data-testid="spreadsheet-note-xlsx-preview"] .text-cell': {
    maxWidth: '100%',
  },
});
